import { useQuery } from "@tanstack/react-query"

import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatAmount } from "@/lib/format"
import { cn } from "@/lib/utils"

import { Advertencias } from "@/routes/holding/Advertencias"
import { AvisoDeAlcance } from "@/routes/holding/AvisoDeAlcance"
import { fetchCaja } from "@/routes/holding/api"
import { useFiltrosGrupo } from "@/routes/holding/HoldingLayout"
import { t } from "@/lib/i18n"

/**
 * Tesoreria de grupo.
 *
 * LA CAJA CONSOLIDADA NO ES LA CAJA DISPONIBLE, y la pantalla esta construida
 * alrededor de eso. Sumar los bancos del grupo y convertir a una moneda da un
 * numero correcto y engañoso: el dinero de Panama no paga la planilla brasilena
 * sin una transferencia que alguien autoriza, que tarda y que paga comision.
 *
 * Por eso el desglose POR SOCIEDAD va primero y el total del grupo va al final,
 * como subtotal. Y por eso lo que no esta asignado a ninguna sociedad no se
 * reparte ni se esconde: se muestra aparte, con la sugerencia al lado.
 */
export function TesoreriaPage() {
  const { moneda } = useFiltrosGrupo()
  const q = useQuery({
    queryKey: ["tesoreria", moneda],
    queryFn: () => fetchCaja(moneda),
  })
  const d = q.data

  return (
    <div className="space-y-3">
      {d ? <Advertencias advertencias={d.advertencias} /> : null}
      {d ? <AvisoDeAlcance alcance={d.alcance} /> : null}

      <DataState
        loading={q.isLoading}
        error={q.error}
        /*
          «Vacio» es que no haya NINGUNA cuenta, no que falte un desglose.
          Miraba solo `por_moneda`, y con alcance parcial ese desglose se oculta
          a proposito: la pantalla decia «no hay cuentas bancarias cargadas» a
          quien tenia su propia caja delante.
        */
        empty={
          !q.isLoading && !q.error && d
            ? d.por_sociedad.length === 0 &&
              d.por_moneda.length === 0 &&
              (d.sin_asignar_cuentas ?? d.sin_asignar.length) === 0
            : false
        }
        emptyMessage={t("No hay cuentas bancarias cargadas.")}
        emptyHint={t("La caja del grupo sale de los saldos bancarios. Cárgalos y Marty los convierte y los suma.")}
        onRetry={() => void q.refetch()}
        columns={[30, 14, 14, 14, 14]}
      >
        {d ? (
          <div className="space-y-3">
            {/* -------------------------------------------- Por sociedad */}
            <WorkPanel
              title={t("Caja por sociedad")}
              meta={`${d.por_sociedad.length} con cuentas asignadas · en ${d.moneda_presentacion}`}
              footnote={t("Ésta es la que decide si una sociedad puede pagar. El total del grupo, abajo, no: mover dinero entre sociedades es una transferencia que alguien tiene que autorizar.")}
            >
              {d.por_sociedad.length === 0 ? (
                <p className="px-3 py-6 text-center text-xs text-muted-foreground">
                  {t("Ninguna cuenta está asignada todavía a una sociedad.")}</p>
              ) : (
                <Table className="table-dense">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[18rem]">{t("Sociedad")}</TableHead>
                      <TableHead className="w-14">{t("País")}</TableHead>
                      <TableHead className="w-20">{t("Moneda")}</TableHead>
                      <TableHead className="w-20 text-right">{t("Cuentas")}</TableHead>
                      <TableHead className="w-40 text-right">
                        {t("Caja (")}{d.moneda_presentacion})
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {d.por_sociedad.map((g) => (
                      <TableRow
                        key={g.entity_id}
                        className={cn(g.agregado && "text-muted-foreground")}
                      >
                        <TableCell
                          className={cn("truncate", g.agregado ? "italic" : "font-medium")}
                          title={g.nota ?? g.razon_social}
                        >
                          {g.razon_social}
                        </TableCell>
                        <TableCell className="code">{g.pais ?? "—"}</TableCell>
                        <TableCell className="code">{g.moneda_funcional ?? "—"}</TableCell>
                        {/*
                          En la fila agregada el numero de cuentas NO se muestra:
                          en cuantos bancos tiene su dinero otra sociedad es
                          parte de lo que no esta a tu alcance. El TOTAL si, que
                          es lo que hace que la columna sume el total del grupo.
                        */}
                        <TableCell className="num text-muted-foreground">
                          {g.agregado
                            ? `${g.sociedades} sociedad(es)`
                            : g.cuentas.length}
                        </TableCell>
                        <TableCell className="num-strong">{formatAmount(g.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </WorkPanel>

            {/* ---------------------------------------------- Por moneda */}
            {/*
              CON ALCANCE PARCIAL ESTE PANEL NO EXISTE, y no es una omision: con
              cuatro sociedades y cuatro monedas funcionales, «el total en PAB»
              es la caja de Panama con otro nombre. El total del grupo se muda
              entonces al pie de la tabla de arriba, porque sigue siendo un dato
              que quien mira tiene derecho a ver.
            */}
            {d.por_moneda_oculto ? (
              <div className="rounded-md border bg-surface-sunken px-4 py-3">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <div>
                    <div className="eyebrow">{t("Caja total del grupo")}</div>
                    <p className="mt-0.5 max-w-[80ch] text-xs text-muted-foreground">
                      {t("El desglose por moneda no está a tu alcance: con una sociedad por moneda funcional, el total de cada moneda es la caja de esa sociedad con otro nombre.")}{(d.sin_asignar_cuentas ?? 0) > 0
                        ? ` Incluye ${formatAmount(d.total_sin_asignar)} de ${d.sin_asignar_cuentas} cuenta(s) sin sociedad asignada.`
                        : ""}
                    </p>
                  </div>
                  <div className="cifra text-xl">
                    {formatAmount(d.total)}{" "}
                    <span className="code text-2xs font-normal text-muted-foreground">
                      {d.moneda_presentacion}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
            <WorkPanel
              title={t("Por moneda")}
              meta={`al ${d.a_fecha}`}
              footnote={t("Al tipo de CIERRE: un saldo es la foto de un día, no un flujo. Se usa la última cotización disponible a esa fecha, no la del día exacto — los fines de semana no cotizan.")}
            >
              <Table className="table-dense">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">{t("Moneda")}</TableHead>
                    <TableHead className="w-20 text-right">{t("Cuentas")}</TableHead>
                    <TableHead className="w-40 text-right">{t("Saldo local")}</TableHead>
                    <TableHead className="w-32 text-right">{t("Tipo de cambio")}</TableHead>
                    <TableHead className="w-40 text-right">
                      {t("En")}{" "}{d.moneda_presentacion}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.por_moneda.map((m) => (
                    <TableRow key={m.moneda}>
                      <TableCell className="code font-medium">{m.moneda}</TableCell>
                      <TableCell className="num text-muted-foreground">{m.cuentas}</TableCell>
                      <TableCell className="num">{formatAmount(m.saldo)}</TableCell>
                      <TableCell className="num text-muted-foreground">
                        {m.tipo_cambio ? formatAmount(Number(m.tipo_cambio), { decimals: 6 }) : "—"}
                      </TableCell>
                      <TableCell className="num-strong">
                        {formatAmount(m.saldo_presentacion)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <tfoot>
                  <TableRow className="bg-surface-sunken">
                    <TableCell colSpan={4} className="rule-double font-medium">
                      {t("Caja total del grupo")}</TableCell>
                    <TableCell className="num-strong rule-double">
                      {formatAmount(d.total)}
                    </TableCell>
                  </TableRow>
                </tfoot>
              </Table>
            </WorkPanel>
            )}

            {/* ------------------------------------------ Sin asignar */}
            {d.sin_asignar.length > 0 ? (
              <WorkPanel
                title={t("Cuentas sin sociedad")}
                meta={`${formatAmount(d.total_sin_asignar)} ${d.moneda_presentacion}`}
                footnote={t("Marty sugiere por nombre y moneda, y no decide: asignar mal pone la caja de una sociedad en otra, y el total del grupo seguiría cuadrando. Las que no tienen sugerencia son genuinamente ambiguas.")}
              >
                <Table className="table-dense">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[16rem]">{t("Cuenta")}</TableHead>
                      <TableHead className="w-20">{t("Moneda")}</TableHead>
                      <TableHead className="w-36 text-right">{t("Saldo")}</TableHead>
                      <TableHead>{t("Sugerencia de Marty")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {d.sin_asignar.map((c) => (
                      <TableRow key={c.bank_account_id}>
                        <TableCell className="truncate font-medium" title={c.nombre}>
                          {c.nombre}
                        </TableCell>
                        <TableCell className="code">{c.moneda}</TableCell>
                        <TableCell className={cn("num", c.saldo === 0 && "num-zero")}>
                          {formatAmount(c.saldo)}
                        </TableCell>
                        <TableCell>
                          {c.sugerencia ? (
                            <span
                              className="inline-flex items-center gap-1.5"
                              title={c.sugerencia.motivo}
                            >
                              <Badge variant="brand-soft">{c.sugerencia.razon_social}</Badge>
                            </span>
                          ) : (
                            <span className="text-2xs text-muted-foreground">
                              {t("Ninguna: el nombre y la moneda no bastan para decidir.")}</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </WorkPanel>
            ) : null}
          </div>
        ) : null}
      </DataState>
    </div>
  )
}
